// Pont de communication entre le front et un composant HTML heberge dans une iframe sandbox.
//
// L'iframe est en `sandbox="allow-scripts"` sans `allow-same-origin` : son origine est opaque, donc
// window.parent / contentWindow ne donnent pas acces aux objets de l'autre cote. Seul postMessage
// est utilisable. Les messages portent `channel: 'pathta'` et un `type` :
//   - 'ready'  : composant -> front, envoye au load de l'iframe
//   - 'call'   : dans les deux sens, appel d'une fonction nommee (id de correlation)
//   - 'result' : reponse a un 'call' (result ou error)
//   - 'event'  : front -> composant, notification sans reponse

export type FrontFunction = (...args: any[]) => any;

const CHANNEL = 'pathta';
const CALL_TIMEOUT_MS = 10000;

// Script injecte dans le HTML du composant. Il expose window.pathta :
//   pathta.call(name, ...args) -> Promise   appelle une fonction du front (liste blanche cote front)
//   pathta.expose(name, fn)                 rend fn appelable par le front (fn peut etre async)
//   pathta.on(eventName, handler)           ecoute un evenement emis par le front
const COMPONENT_SCRIPT = `
(function () {
    var exposed = {}, listeners = {}, pending = {}, nextId = 1;

    function post(message) {
        message.channel = '${CHANNEL}';
        parent.postMessage(message, '*');
    }

    window.addEventListener('message', function (e) {
        var m = e.data;
        if (e.source !== parent || !m || m.channel !== '${CHANNEL}') { return; }

        if (m.type === 'call') {
            var fn = exposed[m.method];
            if (!fn) {
                post({ type: 'result', id: m.id, error: 'Unknown component function: ' + m.method });
                return;
            }
            Promise.resolve()
                .then(function () { return fn.apply(null, m.args || []); })
                .then(
                    function (result) { post({ type: 'result', id: m.id, result: result }); },
                    function (err) { post({ type: 'result', id: m.id, error: String((err && err.message) || err) }); }
                );
        } else if (m.type === 'result') {
            var p = pending[m.id];
            if (!p) { return; }
            delete pending[m.id];
            if (m.error != null) { p.reject(new Error(m.error)); } else { p.resolve(m.result); }
        } else if (m.type === 'event') {
            (listeners[m.name] || []).forEach(function (h) { h(m.data); });
        }
    });

    window.pathta = {
        call: function (name) {
            var args = Array.prototype.slice.call(arguments, 1);
            return new Promise(function (resolve, reject) {
                var id = nextId++;
                pending[id] = { resolve: resolve, reject: reject };
                post({ type: 'call', id: id, method: name, args: args });
            });
        },
        expose: function (name, fn) { exposed[name] = fn; },
        on: function (name, handler) { (listeners[name] = listeners[name] || []).push(handler); }
    };

    window.addEventListener('load', function () { post({ type: 'ready' }); });
})();
`;

// Insere le script du pont le plus tot possible, sans casser le doctype (sinon mode quirks).
export const injectBridgeScript = (html: string): string => {
    const script = `<script>${COMPONENT_SCRIPT}</script>`;

    const head = /<head[^>]*>/i.exec(html);
    if (head) {
        const end = head.index + head[0].length;
        return html.slice(0, end) + script + html.slice(end);
    }

    const doctype = /<!doctype[^>]*>/i.exec(html);
    if (doctype) {
        const end = doctype.index + doctype[0].length;
        return html.slice(0, end) + script + html.slice(end);
    }

    return script + html;
};

interface PendingCall {
    resolve: (value: any) => void;
    reject: (reason: Error) => void;
    timer: ReturnType<typeof setTimeout>;
}

export class ComponentBridge {

    private pending = new Map<number, PendingCall>();
    private nextId = 1;
    private isReady = false;
    private readyWaiters: Array<() => void> = [];

    // frontFunctions est la liste blanche des fonctions du front appelables par le composant.
    constructor(
        private getIframeWindow: () => Window | null,
        private frontFunctions: Record<string, FrontFunction>
    ) { }

    // Demarre l'ecoute des messages ; retourne la fonction d'arret.
    Attach(): () => void {
        window.addEventListener('message', this.onMessage);

        return () => {
            window.removeEventListener('message', this.onMessage);
            this.pending.forEach(p => {
                clearTimeout(p.timer);
                p.reject(new Error('Component bridge detached'));
            });
            this.pending.clear();
        };
    }

    // Front -> composant : appelle une fonction exposee via pathta.expose.
    async Call(method: string, ...args: any[]): Promise<any> {
        await this.waitReady();

        return new Promise((resolve, reject) => {
            const id = this.nextId++;
            const timer = setTimeout(() => {
                this.pending.delete(id);
                reject(new Error(`Component function '${method}' timed out`));
            }, CALL_TIMEOUT_MS);

            this.pending.set(id, { resolve, reject, timer });
            this.post({ type: 'call', id, method, args });
        });
    }

    // Front -> composant : evenement sans reponse, recu via pathta.on.
    Emit(name: string, data?: any): void {
        this.post({ type: 'event', name, data });
    }

    private waitReady(): Promise<void> {
        if (this.isReady) {
            return Promise.resolve();
        }

        return new Promise(resolve => this.readyWaiters.push(resolve));
    }

    private post(message: object) {
        // Origine opaque (sandbox sans allow-same-origin) : '*' est la seule cible possible.
        this.getIframeWindow()?.postMessage({ channel: CHANNEL, ...message }, '*');
    }

    private onMessage = (event: MessageEvent) => {
        const iframeWindow = this.getIframeWindow();
        const m = event.data;

        // Seul le message venant de NOTRE iframe est pris en compte.
        if (!iframeWindow || event.source !== iframeWindow || !m || m.channel !== CHANNEL) {
            return;
        }

        switch (m.type) {
            case 'ready':
                this.isReady = true;
                this.readyWaiters.splice(0).forEach(resolve => resolve());
                break;

            case 'result': {
                const pending = this.pending.get(m.id);
                if (!pending) {
                    return;
                }
                this.pending.delete(m.id);
                clearTimeout(pending.timer);

                if (m.error != null) {
                    pending.reject(new Error(m.error));
                } else {
                    pending.resolve(m.result);
                }
                break;
            }

            case 'call':
                this.handleComponentCall(m.id, m.method, m.args ?? []);
                break;
        }
    };

    // Composant -> front : execute une fonction de la liste blanche et renvoie le resultat.
    private async handleComponentCall(id: number, method: string, args: any[]) {
        const fn = Object.prototype.hasOwnProperty.call(this.frontFunctions, method)
            ? this.frontFunctions[method]
            : undefined;

        if (!fn) {
            this.post({ type: 'result', id, error: `Unknown front function: ${method}` });
            return;
        }

        try {
            const result = await fn(...args);
            this.post({ type: 'result', id, result });
        } catch (err) {
            this.post({ type: 'result', id, error: err instanceof Error ? err.message : String(err) });
        }
    }
}
